/**
 * The search field.
 *
 * It writes two signals: the text in the box, which echoes every keystroke,
 * and the term behind the query, which settles first so a search is not fired
 * per character. Enter commits at once; Escape clears the field without
 * closing the panel the user is typing into.
 *
 * Nothing is fetched from here. Committing a term re-keys the query in the
 * panel below, and that is the whole of it — no cancellation, no sequence
 * number, no request to abandon.
 */

import { type Component, onSettled, Show } from 'solid-js';
import { isTouch } from '../../lib/device.ts';
import { searchText, setSearchTerm, setSearchText } from '../../state/ui.ts';
import { Close, Search } from '../icons/nav.tsx';
import styles from './SearchField.module.css';

const DEBOUNCE_MS = 420;
/** Long enough for the sheet's slide-in to finish before focus moves. */
const FOCUS_DELAY = 340;

export const SearchField: Component = () => {
  let input!: HTMLInputElement;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const commit = (term: string, immediate = false): void => {
    if (timer) clearTimeout(timer);
    if (immediate) setSearchTerm(term.trim());
    else timer = setTimeout(() => setSearchTerm(term.trim()), DEBOUNCE_MS);
  };

  const clear = (): void => {
    setSearchText('');
    commit('', true);
    input.focus();
  };

  onSettled(() => {
    /* A software keyboard covering the results is worse than no focus at all. */
    if (isTouch) return;
    const t = setTimeout(() => input.focus(), FOCUS_DELAY);
    return () => {
      clearTimeout(t);
    };
  });

  onSettled(() => () => {
    if (timer) clearTimeout(timer);
  });

  const onKeyDown = (e: KeyboardEvent): void => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      commit(input.value, true);
      input.blur();
    } else if (e.key === 'Escape') {
      clear();
    }
  };

  return (
    <div class={styles.wrap}>
      <div class={styles.field}>
        <Search />
        <input
          data-testid="search-input"
          ref={input}
          type="search"
          placeholder="Songs, artists, albums"
          autocomplete="off"
          enterkeyhint="search"
          value={searchText()}
          onInput={(e) => {
            setSearchText(e.currentTarget.value);
            commit(e.currentTarget.value);
          }}
          onKeyDown={onKeyDown}
        />
        <Show when={searchText()}>
          <button
            type="button"
            data-testid="search-clear"
            class={styles.clear}
            aria-label="Clear"
            onClick={clear}
          >
            <Close />
          </button>
        </Show>
      </div>
    </div>
  );
};
